import { useState, useCallback, useEffect } from "react";
import { ChevronRightIcon, ChevronDownIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useLibraryBrowser } from "./LibraryBrowserContext";
import type { LibrarySection, LibraryGroup, LibraryCategory } from "./LibraryBrowserContext";

/** Count all items across all groups in a section */
function sectionItemCount(section: LibrarySection): number {
  return section.groups.reduce((sum, g) => sum + g.items.length, 0);
}

/** Count selected items in a section (filter mode) */
function sectionSelectedCount(
  section: LibrarySection,
  selections: Record<string, string[]>
): number {
  // For sections with `--` in the ID (flattened subsections), the selection key
  // is the part before `--` (e.g., "ticker-watchlists-filter--newsquawk-tickers" → "ticker-watchlists-filter")
  const selectionKey = section.id.includes("--") ? section.id.split("--")[0] : section.id;
  const sectionItems = new Set(section.groups.flatMap((g) => g.items.map((i) => i.id)));
  const selected = selections[selectionKey] ?? [];
  // Count only selections that belong to items in this section
  return selected.filter((id) => sectionItems.has(id)).length;
}

/** Group sections into render blocks: individual sections stay as-is, consecutive reference sections merge under a drawer */
function groupSectionsForRender(sections: LibrarySection[]): SidebarRenderBlock[] {
  const blocks: SidebarRenderBlock[] = [];
  let refGroup: LibrarySection[] = [];

  const flushRefGroup = () => {
    if (refGroup.length > 0) {
      blocks.push({ type: "reference-drawer", sections: refGroup });
      refGroup = [];
    }
  };

  for (const section of sections) {
    if (section.sectionVariant === "reference") {
      refGroup.push(section);
    } else {
      flushRefGroup();
      blocks.push({ type: "section", section });
    }
  }
  flushRefGroup();

  return blocks;
}

type SidebarRenderBlock =
  | { type: "section"; section: LibrarySection }
  | { type: "reference-drawer"; sections: LibrarySection[] };

interface SidebarProps {
  /** Override the heading text */
  title?: string;
}

export default function Sidebar({ title }: SidebarProps) {
  const {
    activePath,
    navigateTo,
    sections,
    mode,
    selections,
    selectionsCount,
  } = useLibraryBrowser();

  // Independent expansion state — multiple sections can be open simultaneously
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Auto-expand the section when navigating into it
  useEffect(() => {
    if (activePath[0] && !expandedSections.has(activePath[0])) {
      setExpandedSections((prev) => new Set(prev).add(activePath[0]));
    }
  }, [activePath[0]]);

  const heading = title ?? (mode === "filter" ? "Filters" : "Library");
  const renderBlocks = groupSectionsForRender(sections);

  return (
    <nav className="flex flex-col h-full bg-gray-900 border-r border-gray-700/50 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
      {/* Sidebar header */}
      <div className="px-4 py-3 border-b border-gray-700/50">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {heading}
        </h2>
      </div>

      {/* Section list */}
      <div className="flex-1 py-1">
        {/* Active link — shows when filters are applied */}
        {selectionsCount > 0 && (
          <button
            onClick={() => navigateTo([])}
            className={clsx(
              "w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors focus:outline-none",
              activePath.length === 0
                ? "bg-green-900/10 text-green-400 border-l-2 border-green-500"
                : "text-green-400/70 hover:text-green-400 hover:bg-gray-800/50 border-l-2 border-transparent"
            )}
          >
            <span className="size-3.5 flex-shrink-0" />
            <span className="flex-1 font-medium">Active</span>
            <span className="text-2xs px-1.5 py-0.5 rounded bg-green-900/30 text-green-400 font-medium">
              {selectionsCount}
            </span>
          </button>
        )}

        {renderBlocks.map((block) => {
          if (block.type === "section") {
            return (
              <SidebarSection
                key={block.section.id}
                section={block.section}
                isExpanded={expandedSections.has(block.section.id)}
                onToggleExpand={() => toggleExpanded(block.section.id)}
                activePath={activePath}
                onNavigate={navigateTo}
                selectedCount={
                  mode === "filter"
                    ? sectionSelectedCount(block.section, selections)
                    : undefined
                }
                itemCount={sectionItemCount(block.section)}
              />
            );
          }

          // Reference drawer — visually groups multiple reference sections
          return (
            <ReferenceDrawer
              key="nq-drawer"
              sections={block.sections}
              expandedSections={expandedSections}
              onToggleExpand={toggleExpanded}
              activePath={activePath}
              onNavigate={navigateTo}
              mode={mode}
              selections={selections}
            />
          );
        })}
      </div>
    </nav>
  );
}

// ── Reference Drawer ───────────────────────────────────────────────────
// Visually groups multiple reference-variant sections under a single
// collapsible "Newsquawk Library" header. Each child is still its own
// section internally (preserving selection key mapping).

interface ReferenceDrawerProps {
  sections: LibrarySection[];
  expandedSections: Set<string>;
  onToggleExpand: (id: string) => void;
  activePath: string[];
  onNavigate: (path: string[]) => void;
  mode: string;
  selections: Record<string, string[]>;
}

function ReferenceDrawer({
  sections,
  expandedSections,
  onToggleExpand,
  activePath,
  onNavigate,
  mode,
  selections,
}: ReferenceDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Auto-open if any reference section is active
  const hasActiveChild = sections.some((s) => activePath[0] === s.id);
  const effectiveOpen = isOpen || hasActiveChild;

  // Total selected count across all reference sections
  const totalSelected = sections.reduce(
    (sum, s) => sum + sectionSelectedCount(s, selections),
    0
  );

  // For single-section drawers (e.g., portfolio builder's nq-library), render directly
  if (sections.length === 1) {
    return (
      <SidebarSection
        section={sections[0]}
        isExpanded={expandedSections.has(sections[0].id)}
        onToggleExpand={() => onToggleExpand(sections[0].id)}
        activePath={activePath}
        onNavigate={onNavigate}
        selectedCount={mode === "filter" ? sectionSelectedCount(sections[0], selections) : undefined}
        itemCount={sectionItemCount(sections[0])}
      />
    );
  }

  return (
    <div>
      {/* Separator */}
      <div className="border-t border-gray-700/40 mx-3 mt-3 mb-1" />

      {/* Drawer header */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          "w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors focus:outline-none",
          hasActiveChild
            ? "bg-squawk-blue/8 text-gray-300 border-l-2 border-squawk-blue/70"
            : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30 border-l-2 border-transparent"
        )}
      >
        {effectiveOpen ? (
          <ChevronDownIcon className="size-3.5 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="size-3.5 flex-shrink-0" />
        )}
        <BookOpenIcon className="size-4 flex-shrink-0 text-squawk-blue/60" />
        <span className="flex-1 font-medium truncate text-gray-400">Newsquawk Library</span>
        {totalSelected > 0 && (
          <span className="text-2xs px-1.5 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue font-medium">
            {totalSelected}
          </span>
        )}
      </button>

      {/* Child sections as indented items */}
      {effectiveOpen && (
        <div className="ml-3 border-l border-gray-700/30">
          {sections.map((section) => {
            const isActive = activePath[0] === section.id;
            const selectedCount = mode === "filter" ? sectionSelectedCount(section, selections) : undefined;

            return (
              <button
                key={section.id}
                onClick={() => onNavigate([section.id])}
                className={clsx(
                  "w-full flex items-center gap-2 pl-4 pr-3 py-1.5 text-left text-xs transition-colors focus:outline-none",
                  isActive
                    ? "text-gray-200 bg-squawk-blue/5"
                    : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"
                )}
              >
                <span className="size-3 flex-shrink-0" />
                <span className="flex-1 truncate">{section.label}</span>
                {selectedCount != null && selectedCount > 0 && (
                  <span className="text-2xs px-1.5 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue font-medium">
                    {selectedCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Section ─────────────────────────────────────────────────────────────

interface SidebarSectionProps {
  section: LibrarySection;
  isExpanded: boolean;
  onToggleExpand: () => void;
  activePath: string[];
  onNavigate: (path: string[]) => void;
  selectedCount?: number;
  itemCount: number;
}

function SidebarSection({
  section,
  isExpanded,
  onToggleExpand,
  activePath,
  onNavigate,
  selectedCount,
  itemCount,
}: SidebarSectionProps) {
  const isActive = activePath.length >= 1 && activePath[0] === section.id;
  const Icon = section.icon;
  const isFlatSection = section.groups.length <= 1;
  const hasCategories = !isFlatSection || (section.groups[0]?.categories?.length ?? 0) > 0;
  const isReference = section.sectionVariant === "reference";

  return (
    <div>
      {/* Separator for reference sections */}
      {isReference && (
        <div className="border-t border-gray-700/40 mx-3 mt-3 mb-1" />
      )}

      {/* Section header */}
      <button
        onClick={() => {
          if (hasCategories) onToggleExpand();
          onNavigate([section.id]);
        }}
        className={clsx(
          "w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors focus:outline-none",
          isReference
            ? isActive
              ? "bg-squawk-blue/8 text-gray-300 border-l-2 border-squawk-blue/70"
              : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30 border-l-2 border-transparent"
            : isActive
              ? "bg-squawk-blue/10 text-gray-100 border-l-2 border-squawk-blue"
              : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/50 border-l-2 border-transparent"
        )}
      >
        {/* Only show chevron if there are sub-items to expand */}
        {hasCategories ? (
          isExpanded ? (
            <ChevronDownIcon className="size-3.5 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="size-3.5 flex-shrink-0" />
          )
        ) : (
          <span className="size-3.5 flex-shrink-0" />
        )}
        {isReference && !Icon && <BookOpenIcon className="size-4 flex-shrink-0 text-squawk-blue/60" />}
        {!isReference && Icon && <Icon className="size-4 flex-shrink-0" />}
        <span className={clsx("flex-1 font-medium truncate", isReference && "text-gray-400")}>{section.label}</span>

        {/* Badge: selection count only (no item counts) */}
        {selectedCount != null && selectedCount > 0 && (
          <span className="text-2xs px-1.5 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue font-medium">
            {selectedCount}
          </span>
        )}
      </button>

      {/* Expanded sub-items — only for sections with multiple groups or categories */}
      {isExpanded && hasCategories && !isFlatSection && (
        <div className="ml-3 border-l border-gray-700/30">
          {section.groups.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              sectionId={section.id}
              activePath={activePath}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}

      {/* For flat sections with categories (e.g., Newsquawk Tickers with Indices/Technology), show categories directly */}
      {isExpanded && isFlatSection && (section.groups[0]?.categories?.length ?? 0) > 0 && (
        <div className="ml-3 border-l border-gray-700/30">
          {section.groups[0].categories!.map((cat) => (
            <SidebarCategory
              key={cat.id}
              category={cat}
              sectionId={section.id}
              groupId={section.groups[0].id}
              activePath={activePath}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Group ───────────────────────────────────────────────────────────────

interface SidebarGroupProps {
  group: LibraryGroup;
  sectionId: string;
  activePath: string[];
  onNavigate: (path: string[]) => void;
}

function SidebarGroup({
  group,
  sectionId,
  activePath,
  onNavigate,
}: SidebarGroupProps) {
  const groupPath = [sectionId, group.id];
  const isActive = activePath.length === 2 && activePath[0] === sectionId && activePath[1] === group.id;
  const isExpanded = activePath.length >= 2 && activePath[0] === sectionId && activePath[1] === group.id;
  const hasCategories = group.categories && group.categories.length > 0;

  return (
    <div>
      <button
        onClick={() => onNavigate(isExpanded && activePath.length === 2 ? [sectionId] : groupPath)}
        className={clsx(
          "w-full flex items-center gap-2 pl-4 pr-3 py-1.5 text-left text-xs transition-colors focus:outline-none",
          isActive
            ? "text-gray-100 bg-squawk-blue/5"
            : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"
        )}
      >
        {hasCategories ? (
          isExpanded ? (
            <ChevronDownIcon className="size-3 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="size-3 flex-shrink-0" />
          )
        ) : (
          <span className="size-3 flex-shrink-0" />
        )}
        <span className="flex-1 truncate">{group.label}</span>
      </button>

      {/* Category sub-items */}
      {isExpanded && hasCategories && (
        <div className="ml-4">
          {group.categories!.map((cat) => (
            <SidebarCategory
              key={cat.id}
              category={cat}
              sectionId={sectionId}
              groupId={group.id}
              activePath={activePath}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Category ────────────────────────────────────────────────────────────

interface SidebarCategoryProps {
  category: LibraryCategory;
  sectionId: string;
  groupId: string;
  activePath: string[];
  onNavigate: (path: string[]) => void;
}

function SidebarCategory({
  category,
  sectionId,
  groupId,
  activePath,
  onNavigate,
}: SidebarCategoryProps) {
  const catPath = [sectionId, groupId, category.id];
  const isActive =
    activePath.length === 3 &&
    activePath[0] === sectionId &&
    activePath[1] === groupId &&
    activePath[2] === category.id;

  return (
    <button
      onClick={() => onNavigate(catPath)}
      className={clsx(
        "w-full flex items-center gap-2 pl-8 pr-3 py-1.5 text-left text-xs transition-colors focus:outline-none",
        isActive
          ? "text-squawk-blue bg-squawk-blue/5"
          : "text-gray-400 hover:text-gray-200 hover:bg-gray-800/20"
      )}
    >
      <span className="flex-1 truncate">{category.label}</span>
    </button>
  );
}
